import { useState, useEffect } from "react";
import type { Product } from "../../../../types/product";
import { useCart } from "../../../../context/CartContext";

interface Props {
  product: Product;
  visible: boolean;
}

export default function StickyCartBar({ product, visible }: Props) {
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [rendered, setRendered] = useState(false);
  const { addItem } = useCart();

  // Keep in DOM during exit animation then unmount
  useEffect(() => {
    if (visible) {
      setRendered(true);
    } else {
      const t = setTimeout(() => setRendered(false), 300);
      return () => clearTimeout(t);
    }
  }, [visible]);

  if (!rendered) return null;

  const handleAdd = () => {
    addItem(
      {
        id: product.id,
        name: product.name,
        brand: product.brand,
        price: product.price,
        image: product.image,
      },
      qty
    );
    setAdded(true);
    setTimeout(() => setAdded(false), 2200);
  };

  const savePct = product.originalPrice
    ? Math.round((1 - product.price / product.originalPrice) * 100)
    : null;

  return (
    <div
      className="hidden md:flex fixed top-16 left-0 right-0 z-40 bg-white border-b border-gray-200 items-center gap-5 px-6 py-3 transition-all duration-300"
      style={{
        transform: visible ? "translateY(0)" : "translateY(-110%)",
        opacity: visible ? 1 : 0,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      }}
    >
      {/* Product thumbnail */}
      <div className="w-12 h-12 rounded-lg overflow-hidden border border-gray-100 bg-gray-50 shrink-0">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-contain object-top p-1"
        />
      </div>

      {/* Product info */}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider leading-none mb-0.5">
          {product.brand}
        </p>
        <p className="text-sm font-bold text-gray-900 truncate leading-snug">
          {product.name}
        </p>
        {/* Inline rating */}
        <div className="flex items-center gap-1 mt-0.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <i
              key={s}
              className={`text-[10px] ${
                s <= Math.floor(product.rating)
                  ? "ri-star-fill text-amber-400"
                  : s - product.rating < 1
                  ? "ri-star-half-fill text-amber-400"
                  : "ri-star-line text-gray-300"
              }`}
            />
          ))}
          <span className="text-[11px] text-gray-400 ml-0.5">({product.reviews.toLocaleString()})</span>
        </div>
      </div>

      {/* Price */}
      <div className="shrink-0 text-right">
        <div className="flex items-baseline gap-2">
          <span className={`text-lg font-extrabold ${product.originalPrice ? "text-red-600" : "text-gray-900"}`}>
            ${product.price.toLocaleString()}
          </span>
          {product.originalPrice && (
            <span className="text-sm text-gray-400 line-through">
              ${product.originalPrice.toLocaleString()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 justify-end">
          {savePct && (
            <span className="bg-red-100 text-red-600 text-[10px] font-bold px-1.5 py-0.5 rounded">
              -{savePct}%
            </span>
          )}
          <span className={`text-xs font-medium ${product.inStock ? "text-emerald-600" : "text-red-500"}`}>
            {product.inStock ? "In Stock" : "Out of Stock"}
          </span>
        </div>
      </div>

      {/* Divider */}
      <div className="w-px h-10 bg-gray-100 shrink-0" />

      {/* Trust pills */}
      <div className="hidden lg:flex items-center gap-3 shrink-0">
        <span className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
          <i className="ri-truck-line text-amber-400" /> Free Shipping
        </span>
        <span className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
          <i className="ri-arrow-go-back-line text-amber-400" /> 30-Day Returns
        </span>
      </div>

      {/* Divider */}
      <div className="hidden lg:block w-px h-10 bg-gray-100 shrink-0" />

      {/* Qty stepper */}
      {product.inStock && (
        <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden shrink-0">
          <button
            onClick={() => setQty(Math.max(1, qty - 1))}
            className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <i className="ri-subtract-line" />
          </button>
          <span className="w-9 text-center font-bold text-gray-900 text-sm select-none">{qty}</span>
          <button
            onClick={() => setQty(Math.min(10, qty + 1))}
            className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <i className="ri-add-line" />
          </button>
        </div>
      )}

      {/* Add to Cart CTA */}
      {product.inStock ? (
        <button
          onClick={handleAdd}
          className={`shrink-0 px-6 py-2.5 rounded-xl font-bold text-sm transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap ${
            added
              ? "bg-emerald-500 text-white"
              : "bg-amber-500 hover:bg-amber-400 text-white"
          }`}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={added ? "ri-check-line" : "ri-shopping-cart-line"} />
          </div>
          {added ? "Added!" : "Add to Cart"}
        </button>
      ) : (
        <button className="shrink-0 px-6 py-2.5 rounded-xl border-2 border-amber-400 text-amber-600 font-bold text-sm cursor-pointer hover:bg-amber-50 transition-colors flex items-center gap-2 whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-notification-line" />
          </div>
          Notify Me
        </button>
      )}
    </div>
  );
}
